// @spec DHB-STR-015
// Route page for column mapping interface

import { useAuth } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApplyMappingsResponse } from '@/features/datahub/api/column-mappings';
import { applyColumnMappings } from '@/features/datahub/api/column-mappings';
import { usePeriods } from '@/features/datahub/periods/api/use-periods';
import type { ColumnMapping } from '@/features/datahub/types';
import { ColumnMapper } from '@/features/datahub/uploads';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';

export const Route = createFileRoute('/_authenticated/datahub/uploads/$uploadId/map')({
  component: ColumnMappingPage,
});

function ColumnMappingPage() {
  const { uploadId } = Route.useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { t } = useTranslation('datahub');
  const { organizationId } = useActiveOrganizationId();

  const [isApplying, setIsApplying] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [confirmedMappings, setConfirmedMappings] = useState<ColumnMapping[] | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyMappingsResponse | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Fetch reporting periods
  const { data: periodsData, isLoading: periodsLoading } = usePeriods({
    pageSize: 100,
  });
  const periods = periodsData?.items ?? [];

  // Use first period as default if none selected
  const effectivePeriodId = selectedPeriodId || (periods[0]?.id ?? '');

  const handleComplete = (mappings: ColumnMapping[]) => {
    setConfirmedMappings(mappings);
    setApplyResult(null);
    setApplyError(null);
    toast.success(t('toast.columnsMappedSuccessfully', { count: mappings.length.toString() }));
  };

  const handleApply = async () => {
    if (!effectivePeriodId) {
      toast.error(t('toast.pleaseSelectAReportingPeriodBeforeApplying'));
      return;
    }

    if (!organizationId) {
      toast.error(t('toast.organizationNotAvailable'));
      return;
    }

    setIsApplying(true);
    setApplyResult(null);
    setApplyError(null);

    try {
      const result = await applyColumnMappings(getToken, organizationId, uploadId, {
        reportingPeriodId: effectivePeriodId,
        skipInvalidRows: true,
      });

      setApplyResult(result);

      if (result.errors.length === 0 && result.createdEntries + result.updatedEntries > 0) {
        toast.success(t('toast.dataEntriesApplied', {
          summary: `${String(result.createdEntries + result.updatedEntries)} entries`,
        }));
        // Only navigate away on full success
        void navigate({ to: '/datahub/datapoints' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('toast.failedToApplyMappings');
      setApplyError(message);
      toast.error(message);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = () => {
    void navigate({
      to: '/datahub/upload',
      search: { uploadId: undefined },
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl">
        {/* Breadcrumb/Back navigation */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void navigate({
                to: '/datahub/upload',
                search: { uploadId: undefined },
              })
            }
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('apply.backToUpload')}
          </Button>
        </div>

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold">{t('apply.mapColumns')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('apply.mapColumnsDescription')}
          </p>
        </div>

        {/* Column Mapper Component */}
        <ColumnMapper
          uploadId={uploadId}
          organizationId={organizationId ?? ''}
          onComplete={handleComplete}
          onCancel={handleCancel}
        />

        {/* Apply section - shown after mappings are confirmed */}
        {confirmedMappings !== null && (
          <div className="mt-8 space-y-6">
            <div className="rounded-lg border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('apply.applyMappingsTitle')}</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t('apply.applyMappingsDescription')}
              </p>

              <div className="space-y-4">
                {/* Reporting Period Selector */}
                <div className="max-w-sm">
                  <Label htmlFor="period-select">{t('apply.reportingPeriod')}</Label>
                  {periodsLoading ? (
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('apply.loadingPeriods')}
                    </div>
                  ) : periods.length === 0 ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('apply.noPeriodsAvailable')}
                    </p>
                  ) : (
                    <Select
                      value={effectivePeriodId}
                      onValueChange={setSelectedPeriodId}
                    >
                      <SelectTrigger id="period-select" className="mt-1">
                        <SelectValue placeholder={t('apply.selectPeriodPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {periods.map((period) => (
                          <SelectItem key={period.id} value={period.id}>
                            {period.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Apply Button */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => void handleApply()}
                    disabled={isApplying || !effectivePeriodId || periods.length === 0}
                  >
                    {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isApplying ? t('apply.applying') : t('apply.applyButton')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setConfirmedMappings(null); setApplyResult(null); setApplyError(null); }}
                    disabled={isApplying}
                  >
                    {t('apply.backToMapping')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Apply Error */}
            {applyError && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>{t('apply.errorTitle')}</AlertTitle>
                <AlertDescription>{applyError}</AlertDescription>
              </Alert>
            )}

            {/* Apply Result Summary */}
            {applyResult && (
              <div className="space-y-4">
                {/* Success/Warning summary */}
                <Alert variant={applyResult.errors.length > 0 ? 'default' : 'default'}>
                  {applyResult.errors.length > 0 ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  <AlertTitle>
                    {applyResult.errors.length > 0
                      ? t('apply.resultWithErrors')
                      : t('apply.resultSuccess')}
                  </AlertTitle>
                  <AlertDescription>
                    <div className="flex flex-wrap gap-3 mt-2">
                      <Badge variant="outline">
                        {t('apply.totalRows')}: {applyResult.totalRows}
                      </Badge>
                      {applyResult.createdEntries > 0 && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {t('apply.created')}: {applyResult.createdEntries}
                        </Badge>
                      )}
                      {applyResult.updatedEntries > 0 && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {t('apply.updated')}: {applyResult.updatedEntries}
                        </Badge>
                      )}
                      {applyResult.skippedRows > 0 && (
                        <Badge variant="destructive">
                          {t('apply.skippedRows')}: {applyResult.skippedRows}
                        </Badge>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>

                {/* Detailed error table */}
                {applyResult.errors.length > 0 && (
                  <div className="rounded-lg border bg-card">
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-destructive">
                        {t('apply.validationErrors', { count: applyResult.errors.length })}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('apply.validationErrorsHint')}
                      </p>
                    </div>
                    <div className="max-h-[400px] overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[80px]">{t('apply.errorRow')}</TableHead>
                            <TableHead className="w-[180px]">{t('apply.errorColumn')}</TableHead>
                            <TableHead className="w-[150px]">{t('apply.errorValue')}</TableHead>
                            <TableHead>{t('apply.errorDescription')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {applyResult.errors.map((err, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-sm">
                                {err.rowIndex + 1}
                              </TableCell>
                              <TableCell className="font-medium text-sm">
                                {err.columnName}
                              </TableCell>
                              <TableCell>
                                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                  {err.rawValue || '(empty)'}
                                </code>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {err.error}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
